import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Breadcrumbs } from '@/components';
import { TicketStatusBadge } from '@/components/TicketStatusBadge';
import { ConversationThread } from '@/components/ConversationThread';
import { ReplyForm } from '@/components/ReplyForm';
import { useBrand } from '@/contexts/BrandContext';
import { getRequestDetail, getRequestMessages, updateRequest, getErrorCode } from '@/services/api';
import { Button, ErrorState, LoadingSpinner } from '@airclou/ui';

export function RequestDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { isAuthenticated, viewer } = useBrand();
  const queryClient = useQueryClient();
  const [markingSolved, setMarkingSolved] = useState(false);

  const { data: detail, isLoading: detailLoading, error: detailError } = useQuery({
    queryKey: ['portal-request-detail', ticketId],
    queryFn: () => getRequestDetail(ticketId!),
    enabled: isAuthenticated && !!ticketId,
    staleTime: 30 * 1000,
  });

  const { data: messages, isLoading: messagesLoading, error: messagesError } = useQuery({
    queryKey: ['portal-request-messages', ticketId],
    queryFn: () => getRequestMessages(ticketId!),
    enabled: isAuthenticated && !!ticketId,
    staleTime: 15 * 1000,
  });

  const handleReplySent = () => {
    queryClient.invalidateQueries({ queryKey: ['portal-request-messages', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['portal-request-detail', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['portal-requests'] });
  };

  const handleMarkSolved = async () => {
    if (!ticketId) return;
    setMarkingSolved(true);
    try {
      await updateRequest(ticketId, { status: 'solved' });
      queryClient.invalidateQueries({ queryKey: ['portal-request-detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['portal-requests'] });
    } catch {
      // Error handled silently; badge won't update
    } finally {
      setMarkingSolved(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="max-w-md mx-auto text-center py-16">
          <h1 className="text-2xl font-bold text-foreground mb-2">Sign in required</h1>
          <p className="text-muted-foreground mb-6">Sign in to view this request.</p>
          <Link to={`/auth/login?return_to=${encodeURIComponent(`/requests/${ticketId}`)}`}>
            <Button>Sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (detailError || !detail) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <ErrorState
          description={
            getErrorCode(detailError) === 'NOT_FOUND'
              ? 'This request was not found or you do not have access to it.'
              : 'Failed to load request details.'
          }
          action={
            <Link to="/requests" className="text-primary hover:underline text-sm">
              Back to My requests
            </Link>
          }
        />
      </div>
    );
  }

  const { ticket, customFields, accessType } = detail;
  const isRequester = accessType === 'requester';
  // A reply is stored as an email message, so only email requests take one.
  const canReply = isRequester && ticket.channel === 'email' && ticket.status_category !== 'closed';
  const canMarkSolved = isRequester && !['solved', 'closed'].includes(ticket.status_category);

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'My requests', href: '/requests' },
          { label: ticket.subject },
        ]}
      />

      {/* Back link */}
      <Link
        to="/requests"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to My requests
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket header */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-muted-foreground font-mono">#{ticket.id.replace('tkt_', '')}</span>
              <TicketStatusBadge statusCategory={ticket.status_category} />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">
              {ticket.subject}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Submitted {new Date(ticket.created_at).toLocaleDateString(undefined, {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </p>
          </div>

          {/* Conversation thread */}
          <div>
            <h2 className="text-sm font-medium text-foreground mb-4">Conversation</h2>
            {messagesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : messagesError ? (
              <ErrorState description="Failed to load messages." />
            ) : (
              <ConversationThread
                messages={messages || []}
                requesterEmail={ticket.requester_email}
              />
            )}
          </div>

          {/* Reply form — only for requester, and not if ticket is closed */}
          {canReply && (
            <div className="border-t pt-6">
              <h2 className="text-sm font-medium text-foreground mb-3">Reply</h2>
              <ReplyForm ticketId={ticket.id} onReplySent={handleReplySent} />
            </div>
          )}

          {/* Read-only notice for CC'd / org users */}
          {!isRequester && (
            <div className="border rounded-lg p-4 bg-muted/50 text-sm text-muted-foreground">
              You have read-only access to this request because you are {accessType === 'ccd' ? "CC'd" : 'in the same organization'}.
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {canMarkSolved && (
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Actions</h3>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleMarkSolved}
                disabled={markingSolved}
              >
                {markingSolved ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Mark as solved
              </Button>
            </div>
          )}

          {/* Ticket details */}
          <div className="border rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Details</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Status</dt>
                <dd><TicketStatusBadge statusCategory={ticket.status_category} /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Priority</dt>
                <dd className="capitalize text-foreground">{ticket.priority}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Type</dt>
                <dd className="capitalize text-foreground">{ticket.type}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="text-foreground">{new Date(ticket.created_at).toLocaleDateString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="text-foreground">{new Date(ticket.last_updated_at).toLocaleDateString()}</dd>
              </div>
              {ticket.closed_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Closed</dt>
                  <dd className="text-foreground">{new Date(ticket.closed_at).toLocaleDateString()}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Custom fields */}
          {customFields.length > 0 && (
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Additional information</h3>
              <dl className="space-y-2 text-sm">
                {customFields.map((field) => (
                  <div key={field.field_id} className="flex justify-between">
                    <dt className="text-muted-foreground">{field.title}</dt>
                    <dd className="text-foreground">{field.value || '-'}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
